import {
  Component,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ElementRef,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  AfterViewChecked,
} from '@angular/core';
import { CommonModule, DecimalPipe, NgIf } from '@angular/common';
import { marker } from '../map.component';
import { Autoplay, Navigation, Pagination, Swiper } from 'swiper';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import {
  PhotoResultSet,
  PortalApiService,
  PropertySummary,
} from 'src/app/services/portal-api.service';
import { BehaviorSubject } from 'rxjs';
import { RouterLink } from '@angular/router';
import { Modal, ModalService } from '@indice/ng-components';
import { FavoriteAnonymousModalComponent } from '../../favorite-anonymous-modal/favorite-anonymous-modal.component';
import { AuthService } from '../../../../core/services/auth.service';
import { LoaderComponent } from "../../loader/loader.component";

// Register Swiper modules
Swiper.use([Pagination, Autoplay, Navigation]);

@Component({
  selector: 'app-map-info-window',
  standalone: true,
  imports: [DecimalPipe, CommonModule, TranslocoDirective, RouterLink, LoaderComponent],
  templateUrl: './map-info-window.component.html',
  styleUrl: './map-info-window.component.css',
})
export class MapInfoWindowComponent
  implements OnInit, OnChanges, AfterViewChecked {
  @Input() selectedMarker: marker | undefined;
  @Output() favoritePressed: EventEmitter<void> = new EventEmitter();
  activeLang: string = 'el';
  isLoggedIn: boolean = false;
  private swiper?: Swiper;
  private swiperInitialized = false;
  private translocoService = inject(TranslocoService);
  cardImage: string = '../../../../../../assets/property-apartment.jpg';
  private photosSubject = new BehaviorSubject<PhotoResultSet | undefined>(
    undefined
  );
  photos$ = this.photosSubject.asObservable();

  private propertySubject = new BehaviorSubject<PropertySummary | undefined>(
    undefined
  );
  property$ = this.propertySubject.asObservable();

  private selectedProperty: PropertySummary | undefined = undefined;
  loadingPhotos: boolean = true;;

  constructor(
    private portalService: PortalApiService,
    private elementRef: ElementRef,
    private authService: AuthService,
    private _modalService: ModalService,
    private cdref: ChangeDetectorRef
  ) {
    this.authService.isLoggedIn().subscribe((x) => (this.isLoggedIn = x));
  }

  ngOnInit(): void {
    this.translocoService.langChanges$.subscribe((lang) => {
      this.activeLang = lang;
    });

    // Subscribe to photos$ to initialize Swiper when photos are available
    this.photos$.subscribe((photos) => {
      if (photos?.items?.length) {
        this.swiperInitialized = false; // Reset flag when new photos arrive
      }
    });

    this.property$.subscribe((property) => { });
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('ngOnChanges', changes, this.selectedMarker);
    if (changes['selectedMarker'] && this.selectedMarker?.id) {
      this.portalService
        .getPropertyList(
          true,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          [this.selectedMarker?.id]
        )
        .subscribe({
          next: (res) => {
            console.log('API response for marker', this.selectedMarker, res);
            if (
              res.properties &&
              Array.isArray(res.properties.items) &&
              res.properties.items.length > 0
            ) {
              this.selectedProperty = res.properties.items[0];
              this.propertySubject.next(res.properties.items[0]);
              console.log(
                'Updated propertySubject with',
                res.properties.items[0]
              );
              this.cdref.detectChanges();
            }
          },
        });
      this.loadingPhotos = true;
      this.portalService.getPhotos(this.selectedMarker?.id).subscribe({
        next: (res) => {
          if (res && Array.isArray(res.items) && res.items.length > 1) {
            // Move last item to the beginning of the array
            const items = [...res.items];
            const lastItem = items.pop();
            if (lastItem) {
              items.unshift(lastItem);
              res.items = items;
            }
            if (res.items && res.items.length > 0) {
              res.items = res.items.map((item) => {
                item.fallbackUri = item.uri;
                item.uri = item.uri?.replace(/\.jpg$/, '-s.webp');
                item.uri = item.uri?.replace(/\.jpeg$/, '-s.webp');
                return item;
              })
            }

          }
          this.photosSubject.next(res);
        },
        error: (err) => { },
        complete: () => {
          this.loadingPhotos = false;
          this.cdref.detectChanges();
        },

      });
    }
  }

  private initializeSwiper(): void {
    if (this.swiper) {
      this.swiper.destroy();
    }

    const swiperEl = this.elementRef.nativeElement.querySelector('.swiper');
    if (!swiperEl) {
      return;
    }

    this.swiper = new Swiper(swiperEl, {
      direction: 'horizontal',
      modules: [Pagination, Autoplay, Navigation],
      slidesPerView: 1,
      loop: true,
      centeredSlides: true,
      navigation: {
        prevEl: '.slider-prev-button',
        nextEl: '.slider-next-button',
      },
      pagination: {
        el: '.custom-swiper-pagination',
        bulletClass: 'infowindow-swiper-pagination-bullet',
        bulletActiveClass: 'infowindow-swiper-pagination-bullet-active',
        clickable: true,
        renderBullet: function (index, className) {
          return '<span class="' + className + '"></span>';
        },
      },
    });
    this.swiperInitialized = true;
  }

  toggleFavorite(event: any) {
    event.stopPropagation();
    if (this.isLoggedIn) {
      this.portalService
        .togglePropertyFavorite(this.selectedMarker?.id ?? '')
        .subscribe({
          next: () => {
            if (this.selectedProperty) {
              this.selectedProperty.isFavorite =
                !this.selectedProperty?.isFavorite;
              this.cdref.detectChanges();
            }
          },
          error: (error) => { },
        });
    } else {
      let favoriteAnonymousModal: Modal<FavoriteAnonymousModalComponent> =
        new Modal<FavoriteAnonymousModalComponent>();

      favoriteAnonymousModal = this._modalService.show(
        FavoriteAnonymousModalComponent,
        {
          keyboard: true,
          class: 'h-dvh md:h-auto',
        }
      );
    }
  }

  ngAfterViewChecked() {
    if (!this.swiperInitialized && this.photosSubject.value?.items?.length) {
      const swiperEl = this.elementRef.nativeElement.querySelector('.swiper');
      if (swiperEl) {
        this.initializeSwiper();
        this.swiperInitialized = true;
      }
    }
  }
}
